import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { FormattedMessage } from 'react-intl'
import { get } from 'lodash'
import { wizard, meta } from 'helpers/decorators'
import {
  Input,
  Select,
  Checkbox
} from 'components/fields'
import { Button, FormDebug } from 'components/misc'
import { apiCommon, apiAwardUnits, apiConfiguration, apiLocales } from 'helpers/decorators/api'
import { generalSelectMap } from 'redux/selectors/misc'
import { createMapAwardUnits } from 'redux/selectors/award-units'
import validate from './unit-details.validation'
import { CONFIGURATION_KEYS } from 'constants/enums'

let mapAwardUnits

@apiCommon({
  publicCountries: true,
  rejectEmpty: true
})
@apiAwardUnits()
@apiConfiguration()
@apiLocales()
@wizard({
  form: 'leaderRegistration',
  multipage: true,
  validate,
  disableHook: true,
  destroyOnUnmount: false
})
@meta({
  title: 'Register leader',
  intlKey: 'wizard.unit_details'
})
class UnitDetails extends Component {
  static propTypes = {
    awardUnits: PropTypes.object,
    values: PropTypes.object,
    common: PropTypes.object.isRequired,
    change: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    onFetchAwardUnits: PropTypes.func.isRequired,
    checkOperatingAuthorities: PropTypes.func.isRequired,
    getConfigurationKeyValue: PropTypes.func.isRequired,
    handleGetLeaderRegistrationFieldsCustomization: PropTypes.func.isRequired,
    locales: PropTypes.object,
    changeLocale: PropTypes.func,
    isConfigurationTrue: PropTypes.func
  }

  constructor() {
    super()

    mapAwardUnits = createMapAwardUnits()
    this.state = {
      organizationLanguage: undefined,
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.values && get(this.props, 'values.organizationId')) {
      this.handleLoadFieldCustomizations()
      return
    }

    if (
      get(this.props, 'values.organizationId') &&
      get(this.props, 'values.organizationId') !==
      get(prevProps, 'values.organizationId')
    ) {
      this.handleLoadFieldCustomizations()
    }
  }

  handleFetchAwardUnits = ({
    countryId,
    organizationId,
    parentOrganizationId
  } = {}) => {
    return this.props
      .onFetchAwardUnits(
        { countryId },
        {
          organizationId,
          parentOrganizationId
        }
      )
      .then(({ body }) => {
        this.props.change('helpers.awardUnits', body)
      })
  }

  handleLoadFieldCustomizations = async() => {
    await this.props.handleGetLeaderRegistrationFieldsCustomization(get(this.props.values, 'organizationId'))
  }

  handleSelectCountry = async ({ value } = {}) => {
    if (!value) {
      this.props.change('helpers', {})
      return
    }

    const countries = get(this.props.common, 'countries.data')
    const selectedCountry = countries.filter(item => item.id === value)[0]
    const isPresentOa = get(selectedCountry, 'isPresentOa', false)
    const isPresentNao = get(selectedCountry, 'isPresentNao', false)
    const naoId = get(selectedCountry, 'naoId', false)

    let enableOperatingAuthority

    if (isPresentNao && naoId) {
      const enableOperatingAuthorityConf = await this.props.getConfigurationKeyValue(
        CONFIGURATION_KEYS.enableOperatingAuthority.id,
        naoId
      )

      enableOperatingAuthority = enableOperatingAuthorityConf && enableOperatingAuthorityConf.body.booleanValue

      const definitionRegistrationLocation = await this.props.getConfigurationKeyValue(
        CONFIGURATION_KEYS.definitionRegistrationLocation.id,
        naoId
      )

      if (definitionRegistrationLocation.body.intValue) {
        this.setState({
          organizationLanguage: definitionRegistrationLocation.body.intValue,
        })
      }
    }

    if (isPresentOa && enableOperatingAuthority) {
      this.handleCheckOperatingAuthority({
        countryId: value,
        name: ''
      })
    } else {
      this.handleFetchAwardUnits({
        countryId: value,
        organizationId: get(this.props.values, 'helpers.filterAusById', null)
      })
    }

    this.props.change('helpers', {})
    this.props.change('helpers.selectedCountry', selectedCountry)
    this.props.change('helpers.filterAusById', '')
    this.props.change('organizationId', '')
    this.props.change('helpers.country.enableOperatingAuthority', enableOperatingAuthority)
  }

  handleCheckOperatingAuthority = params => {
    get(params, 'name') &&
      this.props.change(
        'helpers.lastSearchedOperatingAuthority',
        get(params, 'name')
      )

    return this.props.checkOperatingAuthorities(params).then(res => {
      this.props.change('helpers.operatingAuthorities', get(res, 'body'))

      return res
    })
  }

  handleChangeOA = ({ value } = {}) => {
    const { values } = this.props

    if (!value) {
      this.props.change('helpers.selectedOa', {})
    }

    const operatingAuthorities = get(values, 'helpers.operatingAuthorities', [])

    this.props.change('helpers.filterAusById', value)

    if (operatingAuthorities.length === 0) {
      return
    }

    const selectedOa = operatingAuthorities.filter(item => item.id === value)[0]

    const isPresentSoa =
      get(selectedOa, 'subOperatingAuthorities', []).length > 0

    if (!isPresentSoa && value) {
      this.handleFetchAwardUnits({
        countryId: get(values, 'countryId', null),
        parentOrganizationId: get(selectedOa, 'id', null)
      })
    }

    this.props.change('helpers.selectedOa', selectedOa)
  }

  handleChangeSOA = ({ value } = {}) => {
    const { values } = this.props

    if (!value) {
      return
    }

    this.handleFetchAwardUnits({
      countryId: get(values, 'countryId', null),
      parentOrganizationId: value
    })

    this.props.change('helpers.filterAusById', value)
  }

  handleSelectChange = ({ value }) => {
    const { awardUnits } = this.props
    const selectedAU =
      awardUnits.list && awardUnits.list.find(item => item.id === value)

    this.props.change(
      'hierarchyLevel.value',
      get(selectedAU, 'hierarchyLevel.value')
    )
  }

  handleChangeIac = () => {
    this.props.change('helpers.operatingAuthorityId', '')
    this.props.change('helpers.subOperatingAuthorityId', '')
    this.props.change('helpers.selectedOa', '')
    this.props.change('helpers.lastSearchedOperatingAuthority', '')
    this.props.change('organizationId', '')

    this.handleFetchAwardUnits({
      countryId: get(this.props.values, 'countryId', null)
    })
  }

  handleOnSubmit = () => {
    const { organizationLanguage } = this.state
    const { onSubmit, locales, changeLocale } = this.props

    if (organizationLanguage) {
      const updateLocale = locales.data.filter(item => item.id === organizationLanguage)[0]

      try {
        changeLocale(updateLocale)
      } catch (err) {
        console.log(err)
      }
    }

    onSubmit()
  }

  render() {
    const { awardUnits, handleSubmit, common, values } = this.props
    const countryId = get(values, 'countryId')
    const isIac = get(values, 'helpers.isIac') === true
    const lastSearched = get(values, 'helpers.lastSearchedOperatingAuthority')

    const enableOperatingAuthority = get(
      values,
      'helpers.country.enableOperatingAuthority'
    )

    const isPresentIc = get(
      values,
      'helpers.selectedCountry.isPresentIc',
      false
    )

    const isPresentOa = get(
      values,
      'helpers.selectedCountry.isPresentOa',
      false
    )

    const awardUnitsList = get(values, 'helpers.awardUnits', [])

    const isPresentSoa =
      get(values, 'helpers.selectedOa.subOperatingAuthorities', []).length > 0

    const mappedAUs = mapAwardUnits(awardUnitsList, {
      isIac,
      isPresentOa,
      isPresentIc
    })

    const showRole = this.props.isConfigurationTrue(CONFIGURATION_KEYS.organizationContactRole.id)

    return (
      <form onSubmit={handleSubmit(this.handleOnSubmit)}>
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <Select
              label="Select country"
              intlKey="label.select_country"
              name="countryId"
              options={common.countries.list}
              onSelectChange={this.handleSelectCountry}
              loading={get(common, 'countries.loading')}
              searchable
            />

            {isPresentIc &&
              isPresentOa && (
                <Checkbox
                  name="helpers.isIac"
                  label="Select Independent Award Centre"
                  intlKey="label.select_iac"
                  onChange={this.handleChangeIac}
                />
              )}

            {isPresentOa &&
              !isIac && enableOperatingAuthority && (
                <Select
                  label="Select your Operating Authority"
                  intlKey="label.select_your_operating_authority"
                  name="helpers.operatingAuthorityId"
                  disabled={!countryId || !values}
                  promise={this.handleCheckOperatingAuthority}
                  onSelectChange={this.handleChangeOA}
                  options={generalSelectMap(
                    get(values, 'helpers.operatingAuthorities', [])
                  )}
                  autoloadValue={lastSearched}
                />
              )}

            {isPresentSoa &&
              !isIac && enableOperatingAuthority && (
                <Select
                  label="Select sub Operating Authority"
                  name="helpers.subOperatingAuthorityId"
                  placeholder="Start typing..."
                  intlKeyPlaceholder="label.start_typing"
                  intlKey="label.select_sub_operating_authority"
                  options={generalSelectMap(
                    get(
                      values,
                      'helpers.selectedOa.subOperatingAuthorities',
                      []
                    )
                  )}
                  onSelectChange={this.handleChangeSOA}
                  searchable
                />
              )}
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <Select
              key={countryId}
              label="Select your award unit"
              intlKey="label.select_your_award_unit"
              name="organizationId"
              options={mappedAUs}
              loading={awardUnits.loadingUnits}
              disabled={
                !countryId ||
                !values ||
                (isPresentOa &&
                  !get(values, 'helpers.operatingAuthorityId') &&
                  !isIac &&
                  enableOperatingAuthority) ||
                (isPresentSoa &&
                  !get(values, 'helpers.subOperatingAuthorityId') &&
                  !isIac &&
                  enableOperatingAuthority)
              }
              onSelectChange={this.handleSelectChange}
              searchable
            />
          </div>
        </div>
        {showRole && <div className="row">
          <div className="col-xs-12 col-md-6">
            <Input
              label="What is your role in your organisation?"
              intlKey="label.what_is_your_role"
              name="role"
              optional
            />
          </div>
        </div>}
        <div className="row">
        </div>
        <Input type="hidden" name="hierarchyLevel.value" />
        <FormDebug values={values} />
        <div>
          <Button type="submit">
            <FormattedMessage id="button.continue" defaultMessage="Continue" />
          </Button>
        </div>
      </form>
    )
  }
}

export default UnitDetails
